import { useState, useMemo, useEffect } from "react";
import { FaTools } from "react-icons/fa";
import { Table, TableMenu, TableColumn, MenuOption } from "../components/Table";
// import { DashboardHeader } from "../components/DashboardHeader";
import { useModal } from "../context/ConfirmModalContext";
import { useToast } from "../context/ToastContext";
import { PlusIcon } from "@heroicons/react/24/solid";
import User from "../models/User";
import { Maintenance } from "../models/Maintenance";
import { v4 as uuidv4 } from "uuid";
import { getUserHotel } from "../utils/user-helper";
import FullPageSpinner from "../components/FullPageSpinner";
import { formatDateTime } from "../utils/date";
import { sendRequest } from "../utils/api";
import ComplaintDetailsModal from "../Modal/MaintenanceDetailModal";

// TYPES

type Status = "Pending" | "In Progress" | "Resolved";
type Visibility = "private" | "public";



// TABLE COLUMNS

const columns: TableColumn[] = [
    { key: "title", label: "Title", getClassName: () => "capitalize" },
    { key: "createdAt", label: "Created At", responsive: "md" },
    {
        key: "createdBy",
        label: "Created By",
        responsive: "md",
        getClassName: () => "capitalize"
    },
    //   { key: "description", label: "Description", responsive: "md" },
    {
        key: "status",
        label: "Status",
        getClassName: (value: Status) => {
            if (value.toLocaleLowerCase() === "resolved") return "text-green-600 font-semibold";
            else return "text-yellow-500 font-semibold";
        },
    },
];

// const categories = ["All", "Pending", "In Progress", "Resolved"];

const ComplaintPage = () => {
    const { openModal, closeModal } = useModal();
    const { showToast } = useToast();

    // const currentUser: User = { id: "ohigh", name: "John", role: "staff" };

    const [complaints, setComplaints] = useState<Maintenance[]>([]);
    const [loading, setLoading] = useState(false);

    const [user, setUser] = useState({} as any);
    // const [hotel, setHotel] = useState<Hotel>({} as Hotel);

    const [showFormModal, setShowFormModal] = useState(false);

    const [form, setForm] = useState({
        title: "",
        description: "",
        // visibility: "private" as Visibility,
    });
const [selectedComplaint, setSelectedComplaint] = useState(null);
const [isModalOpen, setIsModalOpen] = useState(false);

const handleOpen = (r: any) => {
  setSelectedComplaint(r);
  setIsModalOpen(true);
};

const handleClose = () => {
  setIsModalOpen(false);
  setSelectedComplaint(null);
};

    useEffect(() => {
        async function starterData() {
            setLoading(true);
            try {
                const data = await getUserHotel();
                if (data) {
                    const { user } = data;
                    setUser(user);
                } else {
                    return openModal(
                        "Session Expired ⚠️",
                        "We couldn’t find your user information. Please log in again to continue.",
                        () => {
                            closeModal();
                            window.location.href = "/login";
                        },
                        false,
                        "Login Again"
                    );
                }

                const maintenances = await sendRequest("GET", "/maintenances");
                const mappedMaintenance: Maintenance[] = maintenances.map((mt: any) => ({
                    ...mt,
                    createdBy: (
                        mt.user?.firstName + " " +
                        mt.user?.lastName
                    ),
                    createdAt: formatDateTime(mt.createdAt),
                    status: mt.isResolved ? "Resolved" : "Pending",
                }));
                setComplaints(mappedMaintenance);
            } catch {

            } finally {
                setLoading(false);
            }
        }
        starterData();
    }, []);

    const handleSubmit = async () => {
        setLoading(true);
        try {
            setShowFormModal(false);
            if (!form.title || !form.description) {
                showToast("Fill all fields", { type: "error" });
                return;
            }

            const createdAt = new Date();
            const id = uuidv4();
            const newComplaint: Maintenance = {
                id, title: form.title,
                description: form.description,
                isResolved: false,
            };

            const url = "/maintenances";
            await sendRequest("POST", url, newComplaint)
            setComplaints([
                {
                    ...newComplaint,
                    createdAt: formatDateTime(createdAt),
                    status: "Pending",
                    createdBy: `${user.firstName} ${user.lastName}`,
                },
                ...complaints
            ]);
            setForm({
                title: "",
                description: "",
                // visibility: "private" 
            });
            showToast("Complaint logged successfully", { type: "success" });
        } catch (err: any) {
            showToast("An Error Occured. Please try again.", { type: "error" });
        } finally {
            setLoading(false);
        }
    };


    const maintenanceAction = (id: string, type: "delete" | "update") => {

        if (user.role === "staff") {
            return showToast("Only admin can update status", { type: "error" });
        }

        const isDelete = type === "delete";
        const title = isDelete ? "Delete" : "Update";

        const data = {
            url: `/maintenances/${id}/${title.toLocaleLowerCase()}`,
            warningTitle: title + " Maintenance",
            warningMsg: (
                `Are you sure you want to ${isDelete ? "delete" : "update"} this maintenance?` +
                " This action cannot be undone."
            ),
            data: isDelete ? {} : { isResolved: true },
        };

        openModal(
            data.warningTitle,
            data.warningMsg,
            async () => {
                setLoading(true);
                try {
                    await sendRequest(
                        isDelete
                            ? "DELETE"
                            : "PUT",
                        data.url,
                        data.data
                    );

                    isDelete
                        ? setComplaints((prev) => prev.filter(mt => mt.id !== id))
                        : setComplaints(prev =>
                            prev.map(item =>
                                item.id === id
                                    ? {
                                        ...item,
                                        status: "Resolved",
                                    }
                                    : item
                            )
                        );
                    showToast(
                        `Maintenance ${title.toLocaleLowerCase()} successfully`,
                        { type: "success" }
                    );

                } catch (error: any) {
                    showToast("An error occured. Please try again", { type: "error" });
                } finally {
                    setLoading(false);
                }
            },
            true,
            `Yes, ${title}`,
            "Cancel",
            "red"
        );
    };


    return (
        <div className="p-4">

            {/* Loader */}
            <FullPageSpinner show={loading} />

            <ComplaintDetailsModal
  isOpen={isModalOpen}
  onClose={handleClose}
  data={selectedComplaint}
/>

            {/* HEADER + ADD BUTTON */}
            <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-center justify-between w-full">
                    <h1 className="text-3xl font-bold text-gray-800">Maintenances</h1>

                    <button
                        className="inline-flex items-center gap-2 bg-blue-600 text-white px-3 py-1.5 text-sm font-medium rounded-md hover:bg-blue-700 transition"
                        onClick={() => {
                            setShowFormModal(true)
                        }}
                    >
                        <PlusIcon className="w-4 h-4" />
                        Report
                    </button>

                </div>
            </div>


            {/* MODAL */}
            {showFormModal && (
                <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded w-full max-w-md">
                        <h2 className="text-blue-600 font-semibold mb-3">
                            {FaTools({})} Log Complaint
                        </h2>

                        <input
                            type="text"
                            maxLength={30}
                            placeholder="Title"
                            value={form.title}
                            onChange={(e) => setForm({ ...form, title: e.target.value })}
                            className="border p-2 w-full mb-2"
                        />

                        <textarea
                            placeholder="Description"
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            className="border p-2 w-full mb-2"
                        />

                        <div className="flex justify-end gap-2">
                            <button
                                onClick={() => setShowFormModal(false)}
                                className="px-4 py-2 border rounded"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmit}
                                className="bg-blue-600 text-white px-4 py-2 rounded"
                            >
                                Submit
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* TABLE */}
            <Table
                columns={columns}
                data={complaints}
                tableMenu={(row: Maintenance, closeMenu) => (
                    <TableMenu
                        row={row}
                        options={[
                            {
                                label: "Details",
                                color: "text-blue-500",
                                onClick: (r: Maintenance) => {
handleOpen(r)
                                    // const details = `-------------------------------------------
                                    // title: ${r.title}
                                    // Status: ${r.status}
                                    // Description: ${r.description}
                                    // Created By: ${r.createdBy}
                                    // Created At: ${r.createdAt}
                                    // -------------------------------------------`;
                                    // openModal(
                                    //     "Complaint Details",
                                    //     details,
                                    //     closeModal,
                                    //     false,
                                    //     "Close"
                                    // );
                                },
                            },
                            user.role !== "staff"
                                ? {
                                    label: "Mark Resolved",
                                    color: "text-green-600",
                                    onClick: (r: Maintenance) => maintenanceAction(r.id, "update")
                                } : null,

                            user.role !== "staff"
                                ? {

                                    label: "Delete",
                                    color: "text-red-500",
                                    onClick: (r: Maintenance) => {
                                        maintenanceAction(r.id, "delete");
                                        // if (r.paymentStatus.toLowerCase() === "paid") {
                                        // const isAccess = checkAccess(
                                        //     "forceDeleteOrder", access, openModal, closeModal
                                        // );
                                        // if (!isAccess) return;
                                    },
                                } : null
                        ].filter(Boolean) as MenuOption[]}
                        closeMenu={closeMenu}
                    />
                )}
            />
        </div>
    );
};

export default ComplaintPage;